import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { fetchWstrzymane, przywrocWstrzymane, rozeslijWstrzymane, type Wstrzymanie } from '@/lib/wstrzymane-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WSTRZYMANE ZDJĘCIA — „pokazuje, co się zmienia, i pozwala potwierdzić" (użytkownik, 24.09.2026).
 *
 *  ⚠️⚠️ PANEL ISTNIEJE TYLKO, GDY COŚ CZEKA. Zwykle nie czeka nic, a stały pusty panel na górze
 *  zakładki uczyłby, że tego miejsca nie trzeba czytać. Licznik przy zakładce mówi to samo z daleka.
 *
 *  ⚠️ DWA WYJŚCIA I OBA Z POTWIERDZENIEM: „Roześlij" zdejmuje we wszystkich bazach, „Przywróć"
 *  oddaje wiersze maszynie, która je zdjęła. Oba zapisują do baz klienta na całej wyspie.
 *
 *  ⚠️ BŁĄD ODCZYTU NIE RYSUJE NICZEGO. Panel dotyczy rzadkiego zdarzenia; ramka z błędem stojąca
 *  na stałe (np. starsza instancja bez tej trasy) zasłaniałaby resztę zakładki. Wstrzymanie
 *  i tak zostawia wpis w dzienniku wyspy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function WstrzymaneUsuniecia({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const dane = useQuery({
    /** ⚠️ Ten sam klucz co licznik przy zakładce w `SmartSync.tsx` — jedno zapytanie na oba miejsca. */
    queryKey: ['wstrzymane', groupId],
    queryFn: () => fetchWstrzymane(groupId),
    refetchInterval: 30_000,
  });
  const lista = dane.data?.wstrzymane ?? [];
  if (lista.length === 0) return null;

  return (
    <section className="space-y-2 rounded-lg border border-[var(--warning)]/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldAlert className="size-4 text-[var(--warning)]" />
        <h2 className="text-sm font-medium">{t('wstrz.tytul')}</h2>
        <Badge variant="warning">{t('wstrz.czeka')}</Badge>
      </div>
      <ul className="space-y-2">
        {lista.map((w) => (
          <Pozycja key={w.id} groupId={groupId} w={w} />
        ))}
      </ul>
    </section>
  );
}

type Akcja = 'rozeslij' | 'przywroc';

function Pozycja({ groupId, w }: { groupId: string; w: Wstrzymanie }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [pytanie, setPytanie] = useState<Akcja | null>(null);
  const [lista, setLista] = useState(false);

  const decyzja = useMutation({
    mutationFn: (a: Akcja) => (a === 'rozeslij' ? rozeslijWstrzymane(groupId, w.id) : przywrocWstrzymane(groupId, w.id)),
    onSuccess: async (r) => {
      toast.success(t('wstrz.zakolejkowano').replace('{n}', String(r.zakolejkowanych)));
      setPytanie(null);
      await qc.invalidateQueries({ queryKey: ['wstrzymane', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const kto = w.maszyna ?? w.nodeId;
  const rodzaj = w.rodzaj === 'AUTOMAT' || w.rodzaj === 'GRUPA' || w.rodzaj === 'MPK' ? w.rodzaj : 'INNE';

  return (
    <li className="space-y-2 rounded border border-border px-2 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          {t('wstrz.naglowek')
            .replace('{kto}', kto)
            .replace('{n}', String(w.wierszy))
            .replace('{co}', t(`wstrz.co.${rodzaj}`))}
        </span>
        <span className="text-muted-foreground">{new Date(w.od).toLocaleString('pl')}</span>
        <span className="font-mono text-[11px] text-muted-foreground">{w.tabela}</span>
      </div>
      <p className="text-[11px] text-muted-foreground">{t('wstrz.pozostaleMaja')}</p>

      {/* ⚠️ Podsumowanie na cel PRZED listą — przy tysiącu wierszy od niego zaczyna się ocena. */}
      <div className="flex flex-wrap gap-1">
        {w.cele.map((c) => (
          <Badge key={c.nazwa} variant="neutral">
            {c.nazwa}: {c.wierszy}
          </Badge>
        ))}
      </div>

      <Button size="sm" variant="ghost" className="h-6 px-1 text-[11px]" onClick={() => setLista(!lista)}>
        {lista ? t('wstrz.ukryj') : t('wstrz.lista')}
      </Button>
      {lista && (
        <div className="max-h-72 overflow-auto rounded border border-border">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-background text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1 font-medium">{t('wstrz.kolNumer')}</th>
                <th className="px-2 py-1 font-medium">{t('wstrz.kolOsoba')}</th>
                <th className="px-2 py-1 font-medium">{t(`wstrz.kolCel.${rodzaj}`)}</th>
              </tr>
            </thead>
            <tbody>
              {w.zmiany.map((z, i) => (
                <tr key={`${z.numer}|${z.cel}|${i}`} className="border-t border-border">
                  <td className="px-2 py-1 font-mono">{z.numer}</td>
                  <td className="px-2 py-1">{z.osoba ?? '—'}</td>
                  <td className="px-2 py-1">{z.cel}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {w.obcietych > 0 && (
            <p className="px-2 py-1 text-[11px] text-muted-foreground">
              {t('wstrz.jeszcze').replace('{n}', String(w.obcietych))}
            </p>
          )}
        </div>
      )}

      {pytanie === null ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="destructive" onClick={() => setPytanie('rozeslij')}>
            {t('wstrz.rozeslij')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPytanie('przywroc')}>
            {t('wstrz.przywroc')}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* ⚠️ Skutek nazwany PRZED kliknięciem — obie decyzje zapisują do baz klienta. */}
          <p className="text-[11px] font-medium">
            {(pytanie === 'rozeslij' ? t('wstrz.pytanieRozeslij') : t('wstrz.pytaniePrzywroc'))
              .replace('{n}', String(w.wierszy))
              .replace('{kto}', kto)}
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={pytanie === 'rozeslij' ? 'destructive' : 'default'}
              disabled={decyzja.isPending}
              onClick={() => decyzja.mutate(pytanie)}
            >
              {decyzja.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : pytanie === 'rozeslij' ? (
                t('wstrz.takRozeslij')
              ) : (
                t('wstrz.takPrzywroc')
              )}
            </Button>
            <Button size="sm" variant="outline" disabled={decyzja.isPending} onClick={() => setPytanie(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
